import React, { useState, useCallback, useEffect } from 'react';
import { IconButton, Icon, TextField, InputAdornment, List, ListItem, Collapse, ListItemText, ListItemIcon } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  StyledDialog,
  RootWrap,
  TT_FORM_STYLE,
  TT_TITLE_STYLE,
  TT_SCREEN_WIDTH,
  TT_SCREEN_CONTAINER,
  TT_SCREEN_CONTAINER_MOBILE,
  Container,
  Subtitle,
  InputContainer,
  SearchContainer,
  ListWrap,
  ListRow,
  MarginLeftList,
  MarginRightIcon,
  TagContainerBtn,
  CheckpointText,
} from './TaskTag.styles';
import { get, size, set } from 'global/lib/lodash';
import Form from 'global/components/form';
import { useScreen, Loader } from 'global/styles';
import { Alert } from '@mui/material';
import useRetrieveData from 'global/hooks/useRetrieveData';
import gstyles from 'global/styles';
import getHighlightedText from 'global/components/highlight-text';
import ButtonToggle from 'global/components/btn-toggle';
import useDebounce from 'global/hooks/useDebounce';
import { sortItemsAlphabetically } from 'global/utils';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from '../../admin/account-settings';

const scorecardsOpts = {
  build: ['scorecards'],
  useEffect: true,
  useCache: {
    reload: 20,
  },
};

const TagButton = (props) => {
  const { isActive } = props;

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    props.onClick && props.onClick();
  };

  return (
    <TagContainerBtn onClick={onClick} isActive={isActive}>
      <Icon>label</Icon>
    </TagContainerBtn>
  );
};

const System = (props) => {
  const [open, setOpen] = useState(false);

  const { system } = props;
  const title = get(system, 'name', '');
  const checklists = get(system, 'checklists', []);

  useEffect(() => {
    const match = get(props, 'matches.term');
    if (match) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [props.matches]);

  return (
    <>
      <ListRow onClick={() => setOpen(!open)}>
        <ListItemText primary={title} slotProps={{ primary: { variant: 'button' } }} />
        <ListItemIcon>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListRow>
      <Collapse in={open} unmountOnExit>
        <MarginLeftList disablePadding>
          {checklists.map((check, i) => (
            <Checklist {...props} key={get(check, 'checklistId')} checklist={check} />
          ))}
        </MarginLeftList>
      </Collapse>
    </>
  );
};

const Checklist = (props) => {
  const [open, setOpen] = useState(false);

  const { checklist } = props;
  const title = get(checklist, 'name', '');

  const checkpoints = get(checklist, 'checkpoints', []);

  useEffect(() => {
    const match = get(props, 'matches.term');
    if (match) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [props.matches]);

  const tagMe = useCallback((checkpoint) => {
    props.changeTag({
      ...checkpoint,
      checkpointId: get(checkpoint, 'id'),
      checklistId: get(checklist, 'id'),
      score: get(checkpoint, 'selectedOption.value'),
      criteria: [get(checkpoint, 'description'), get(checkpoint, 'selectedOption.criteria')].filter(Boolean).join(' '),
      scorecardConfig: get(checkpoint, 'selectedOption.scorecardConfig'),
      pssvChecklist: { ...checklist },
      pssvCheckpoint: { ...checkpoint },
    });
  }, []);

  return (
    <>
      <ListRow onClick={() => setOpen(!open)}>
        <ListItemText primary={title} />
        <MarginRightIcon>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </MarginRightIcon>
        <TagButton onClick={() => tagMe()} />
      </ListRow>
      {!props.thinking && (
        <Collapse in={open} unmountOnExit>
          <List disablePadding>
            {sortItemsAlphabetically(checkpoints).map((check) => (
              <Checkpoint key={get(check, 'checkpointId')} matches={props.matches} checkpoint={check} onClick={tagMe} {...props} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const Checkpoint = (props) => {
  const { checkpoint, matches } = props;
  const title = get(checkpoint, 'name', '');
  const description = get(checkpoint, 'description');

  var variant = size(get(checkpoint, 'scorecardOptions', [])) > 2 ? 'normal' : 'passFail';
  var options = get(checkpoint, 'scorecardOptions', [])
    .sort((a, b) => a.value - b.value)
    .map((o, i) => ({
      criteria: get(o, 'criteria'),
      index: i,
      value: get(o, 'value', null),
    }));

  const selectOption = (vals) => {
    props.onClick({
      ...checkpoint,
      selectedOption: { ...vals, scorecardConfig: get(props, 'scorecardConfig', {}) },
    });
  };

  return (
    <ListItem>
      <CheckpointText
        primary={getHighlightedText(`${title}: ${description || ''}`, matches.term)}
        secondary={
          <ButtonToggle
            variant={variant}
            options={options}
            selected={undefined}
            onSelect={selectOption}
            getHighlight={(x) => getHighlightedText(x, matches.term)}
            scorecardConfig={get(props, 'scorecardConfig', {})}
          />
        }
      />
    </ListItem>
  );
};

export const TaskTag = (props) => {
  const [formLoading] = useState(false);
  const [error, setError] = useState();
  const [tags, setTags] = useState({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);
  const [scorecardId, setScorecardId] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [textMatches, setTextMatches] = useState({
    systems: [],
    term: '',
  });
  const [systems, setSystems] = useState([]);

  const styles = gstyles();
  const { isMobileXS } = useScreen();

  const handleClose = () => {
    props.onClose();
  };

  const defaultScorecard = getAccountSetting(SETTINGS.assessmentScorecard);

  const scorecards = useRetrieveData({ url: `/scorecards` }, scorecardsOpts);
  const scData = useRetrieveData(
    { url: `/scorecards/${scorecardId}` },
    {
      waitFor: scorecardId,
      build: ['scorecards'],
      params: {
        include: 'scorecardOptions,checklists,checklists.checkpoints,checklists.classification,checklists.classification.system',
      },
      useCache: { reload: 20 },
      transformData: ([data]) => {
        const { scorecardOptions, checklists, ...rest } = data;

        const scorecardMap = new Map();
        for (const sco of scorecardOptions) {
          const key = `${sco.checklistId}:${sco.checkpointId}`;
          if (!scorecardMap.has(key)) {
            scorecardMap.set(key, []);
          }
          scorecardMap.get(key).push(sco);
        }

        return [{
          ...rest,
          checklists: checklists.map((checklist) => ({
            ...checklist,
            checkpoints: checklist.checkpoints.map((checkpoint) => ({
              ...checkpoint,
              scorecardOptions:
                scorecardMap.get(`${checklist.id}:${checkpoint.id}`) || [],
            })),
          })),
        }];
      },
    }
  );

  const formUpdate = (vals) => {
    setScorecardId(vals.scorecard);
  };

  const reset = () => {
    setTags({});
    setTextMatches({
      systems: [],
      checklists: [],
      term: '',
    });
    setSearch('');
    setThinking(false);
    setSystems([]);
  };

  const changeTag = useCallback((tag) => {
    setTags(tag);
  }, []);

  const searchChange = (val) => {
    setSearch(val);
    setThinking(true);
  };

  useEffect(() => {
    if (Object.keys(tags).length > 0) {
      props.onTagSelected({
        ...tags,
        scorecardId,
      });
      props.onClose();
      reset();
    }
  }, [tags]);

  // The component stays mounted while the dialog toggles, so a previously
  // selected scorecard would otherwise carry over to the next open. Reset on
  // close (not open — the Form mounts and reads its defaultValue before an
  // on-open effect could clear the stale value) so the next open starts from
  // the account-setting default again.
  useEffect(() => {
    if (!props.open) {
      setScorecardId(null);
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    const sys = {};
    if (scData.data) {
      const clists = get(scData, 'data.0.checklists', []);
      clists.forEach((clist) => {
        const syst = get(clist, 'classification.system', {});
        const listCheck = get(sys, [syst.id, 'checklists'], []);
        set(sys, syst.id, syst);
        set(sys, [syst.id, 'checklists'], [...listCheck, clist]);
      });
      const newSystems = [];
      for (let k in sys) {
        newSystems.push({ ...sys[k] });
      }
      setSystems(newSystems);
    }
  }, [scData.data]);

  useEffect(() => {
    const term = (search || '').toLowerCase();
    if (term) {
      const newSystems = systems
        .map((sys) => {
          const checklists = get(sys, 'checklists', []);

          const newChecklists = checklists
            .map((cl) => {
              const cps = get(cl, 'checkpoints', []);

              const newCheckpoints = cps
                .map((cp) => {
                  const desc = (get(cp, 'description', '') || '').toLowerCase();
                  const chpOptDes = get(cp, 'scorecardOptions', [])
                    .map((cpo) => get(cpo, 'criteria', ''))
                    .join(' ');

                  if (desc.indexOf(term) > -1 || chpOptDes.indexOf(term) > -1) {
                    return cp;
                  } else {
                    return null;
                  }
                })
                .filter((cp) => cp);

              return size(newCheckpoints) ? { ...cl, checkpoints: newCheckpoints } : null;
            })
            .filter((cl) => cl);

          return size(newChecklists) ? { ...sys, checklists: newChecklists } : null;
        })
        .filter((sys) => sys);

      setTextMatches({
        systems: newSystems,
        term,
      });
    } else {
      setTextMatches({ systems: [], term: '' });
    }
    setTimeout(() => {
      setThinking(false);
    }, 200);
  }, [debouncedSearch]);

  const showBlank = size(textMatches.term) > 0 && !size(textMatches.systems);

  return (
    <StyledDialog className="task-tag-dialog" disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <RootWrap>
        <Container>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          <Subtitle variant="subtitle2">
            LINK TO LIBRARY
          </Subtitle>

          <InputContainer>
            <Form
              loading={formLoading}
              showAllScreens={true}
              formStyle={TT_FORM_STYLE}
              onFormState={formUpdate}
              screens={[
                {
                  title: '',
                  titleStyle: TT_TITLE_STYLE,
                  screenContainerStyle: isMobileXS ? TT_SCREEN_CONTAINER_MOBILE : TT_SCREEN_CONTAINER,
                  screenContainer: {
                    props: {
                      className: TT_SCREEN_WIDTH,
                    },
                  },
                  inputs: [
                    {
                      id: 'scorecard',
                      type: 'Select',
                      data: () => (scorecards.data || []).map((d) => ({ title: d.name, value: d.id })),
                      defaultValue: () => scorecardId || defaultScorecard,
                      props: {
                        label: scorecards.loading ? 'Loading...' : 'Scorecard',
                        // Dialog sits at zIndex 1400 (above the parent full-screen modal);
                        // lift this Select's dropdown menu above it so options aren't hidden behind the dialog.
                        MenuProps: { sx: { zIndex: 1500 } },
                      },
                    },
                  ],
                },
              ]}
            />

            <SearchContainer>
              <TextField
                variant="outlined"
                fullWidth
                id="search"
                value={search}
                label="Search"
                onChange={(e) => searchChange(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">{thinking ? <Loader size={20} /> : <Icon>search</Icon>}</InputAdornment>,
                  },
                }}
              />
            </SearchContainer>

            <InputContainer>
              <ListWrap>
                {!scorecards.loading && !!scData.loading && !!scorecardId && (
                  <div className={styles.centerColumn}>
                    <Loader />
                  </div>
                )}

                {!!showBlank && (
                  <div className={styles.centerColumn} style={{ padding: 20 }}>
                    <span>{`There are no results that meet your search criteria.`}</span>
                  </div>
                )}

                {!scData.loading && !!size(systems) && !showBlank && (
                  <List component="nav">
                    {(size(textMatches.systems) ? textMatches.systems : systems).map((sys, i) => (
                      <System key={i} changeTag={changeTag} system={sys} matches={textMatches} thinking={thinking} scorecardConfig={get(scData, 'data.0.config', {})} />
                    ))}
                  </List>
                )}
              </ListWrap>
            </InputContainer>
          </InputContainer>
        </Container>
      </RootWrap>
    </StyledDialog>
  );
};

export default TaskTag;
